import { memo, useState, useCallback, useEffect, useLayoutEffect, useRef, Suspense, lazy, type ReactNode } from 'react';
import { Outlet, useParams, useLocation } from 'react-router-dom';
import { Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ActiveLessonProvider } from '@/contexts/ActiveLessonContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { SplitView, type PanelImperativeHandle } from '@/components/atoms/SplitView';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/atoms/Tabs';
import { CourseOnboardingCard } from '@/components/molecules/CourseOnboardingCard';
import { CourseNotFound } from '@/components/molecules/classroom/CourseNotFound';
import { KeyboardShortcutCheatsheet } from '@/components/molecules/KeyboardShortcutCheatsheet';
import { isValidUuid } from '@/lib/uuid';
import { WorkspaceHeader } from '@/components/molecules/classroom/WorkspaceHeader';
import { WorkspaceBreadcrumbProvider } from '@/contexts/WorkspaceBreadcrumbContext';
import { useWorkspaceBreadcrumb } from '@/contexts/useWorkspaceBreadcrumb';
import { COURSE_LAYOUT, WORKSPACE_TOKENS } from '@/lib/constants';
import {
  computeTutorMinPct,
  EXPANDED_MIN_CONTAINER_PX,
  TUTOR_MIN_PX_EXPORT as TUTOR_MIN_PX,
  TUTOR_DEFAULT_PCT,
} from '@/components/templates/CourseLayout/useTutorPaneState';
import { migrateTutorSplitStorage } from '@/components/templates/CourseLayout/migrateTutorSplitStorage';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import type { Layout } from 'react-resizable-panels';

import type { CourseLayoutRightPaneMode, CourseLayoutVariant } from './types';

const AiTutorPanel = lazy(() =>
  import('@/components/organisms/classroom/AiTutorPanel').then(m => ({ default: m.AiTutorPanel }))
);
const CourseOutlineSidebar = lazy(() =>
  import('@/components/molecules/classroom/CourseOutlineSidebar').then(m => ({ default: m.CourseOutlineSidebar }))
);
const AuthorOutlineSidebar = lazy(() =>
  import('@/components/molecules/classroom/AuthorOutlineSidebar').then(m => ({ default: m.AuthorOutlineSidebar }))
);

function readPanelState(key: string, defaultOpen: boolean): boolean {
  try {
    const saved = localStorage.getItem(key);
    if (saved === null) return defaultOpen;
    return saved === 'open';
  } catch {
    return defaultOpen;
  }
}

function savePanelState(key: string, open: boolean) {
  try {
    localStorage.setItem(key, open ? 'open' : 'closed');
  } catch {
    // localStorage unavailable
  }
}

interface CourseLayoutProps {
  /** Chrome variant. `student` (default) mounts the classroom outline sidebar;
   *  `author` mounts the editor outline sidebar (inline rename + dnd reorder +
   *  add-lesson per chapter). Per-variant child routes read `variant` via
   *  `useOutletContext<CourseLayoutContext>()`. */
  variant?: CourseLayoutVariant;
}

/**
 * CourseLayout — three-panel wrapper for /classroom/course/:id/* routes.
 *
 * Desktop: SplitView with content + always-visible AI Tutor pane.
 *   - User can drag the splitter to resize chat width (clamped to TUTOR_MIN_PX
 *     pixel floor and 50% max). Width persists across reloads.
 *   - The pane CANNOT be hidden, collapsed, or railed. It is always rendered.
 *
 * Phone (<768px): Outlet full-width with bottom-sheet tutor behind a FAB
 *   (existing pattern — unchanged by the desktop simplification).
 *
 * AiTutorPanel mounts once, keyed by subjectId, persists across navigation.
 *
 * Variant ('student' | 'author'):
 *   - student: classroom viewer (current behavior). Sidebar is read-only,
 *     shows progress + lock icons, navigates to /classroom/course/...
 *   - author: editor workspace. Sidebar supports inline lesson rename,
 *     drag-reorder within a chapter, and add-lesson per chapter; navigates
 *     to /course/:subjectId/lesson|quiz/... Step 22 adds the variant; Step 23
 *     wires LessonEditorRoute to actually mount with variant='author'.
 */
const CourseLayoutComponent = ({ variant = 'student' }: CourseLayoutProps) => {
  const { subjectId, lessonId, quizId, chapterId } =
    useParams<{ subjectId: string; lessonId?: string; quizId?: string; chapterId?: string }>();
  const location = useLocation();

  // Step 25 (R7.6): content pane fade-in keys off the active content id so
  // same-route param changes (lesson↔lesson, lesson↔quiz, chapter↔chapter in
  // ontology editor) remount the keyed wrapper and the CSS animation replays.
  // Falls back to pathname for routes that don't carry any of the three param
  // variants (e.g., course root).
  const contentKey = lessonId ?? quizId ?? chapterId ?? location.pathname;

  // Step 61 (PR-A11Y.E): zero out the inline animation/transition durations
  // wired into the content-fade wrapper + outline aside when the user has
  // requested reduced motion. The CSS-only `*!important` rule in index.css
  // (GAP-65) loses the cascade contest to inline `style={{ ... }}` durations
  // and to `@layer utilities`-scoped Tailwind utilities, so the components
  // themselves must opt into reduce. WCAG 2.3.3 (AAA).
  const prefersReducedMotion = useReducedMotion();

  const [outlineOpen, setOutlineOpen] = useState(() =>
    readPanelState(COURSE_LAYOUT.PANEL_STORAGE_KEY_OUTLINE, true)
  );

  // Mobile bottom-sheet tutor open/closed. Desktop pane is always visible.
  const [tutorOpen, setTutorOpen] = useState(() =>
    readPanelState(COURSE_LAYOUT.PANEL_STORAGE_KEY_TUTOR, true)
  );

  // Imperative ref for the resizable tutor pane — used to enforce the
  // pixel-floor clamp on splitter drag and to rescue poisoned storage layouts.
  const tutorPanelRef = useRef<PanelImperativeHandle | null>(null);

  // SYNCHRONOUS migration — must run BEFORE SplitView reads localStorage.
  // (See history: react-resizable-panels reads storage during render, so a
  // post-mount effect would be too late to repair a poisoned [97, 3] split.)
  const [storageMigrationOutcome] = useState(() => {
    if (typeof window === 'undefined') return 'no-op-no-storage' as const;
    return migrateTutorSplitStorage(window.localStorage, window.innerWidth);
  });

  const splitContainerRef = useRef<HTMLDivElement>(null);
  const rafIdRef = useRef<number | null>(null);

  // Dynamic pixel-to-percent floor for the tutor pane (TUTOR_MIN_PX = 320).
  const [tutorMinPct, setTutorMinPct] = useState<number>(() => {
    if (typeof window === 'undefined') return TUTOR_DEFAULT_PCT;
    return computeTutorMinPct(window.innerWidth);
  });

  const scheduleViewportCheck = useCallback(() => {
    if (rafIdRef.current !== null) return;
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      const container = splitContainerRef.current;
      if (!container) return;
      const containerPx = container.clientWidth;
      if (containerPx < 400) return;

      const nextPct = computeTutorMinPct(containerPx);
      setTutorMinPct((prev) => (prev === nextPct ? prev : nextPct));

      // Defensive rescue: if the DOM rendered tutor below the pixel floor
      // (e.g. window resize squashed it), force-resize to the floor.
      if (containerPx >= EXPANDED_MIN_CONTAINER_PX) {
        const tutorEl = container.querySelector<HTMLElement>('#tutor');
        const tutorPx = tutorEl?.getBoundingClientRect().width ?? 0;
        if (tutorPx > 0 && tutorPx < TUTOR_MIN_PX) {
          tutorPanelRef.current?.resize(`${Math.max(TUTOR_DEFAULT_PCT, nextPct)}%`);
        }
      }
    });
  }, []);

  // Post-mount rescue: if the migration cleared poisoned storage OR the DOM
  // currently violates the pixel floor, restore to the default split.
  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      const container = splitContainerRef.current;
      if (!container) return;
      const containerPx = container.clientWidth;
      if (containerPx < EXPANDED_MIN_CONTAINER_PX) return;
      const tutorEl = container.querySelector<HTMLElement>('#tutor');
      const tutorPx = tutorEl?.getBoundingClientRect().width ?? 0;
      if (
        storageMigrationOutcome === 'cleared-poisoned-layout' ||
        (tutorPx > 0 && tutorPx < TUTOR_MIN_PX)
      ) {
        tutorPanelRef.current?.resize(`${TUTOR_DEFAULT_PCT}%`);
      }
    });
    return () => cancelAnimationFrame(rafId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Real-time clamp on every drag/keyboard nudge/programmatic setLayout:
  // the tutor pane never renders below TUTOR_MIN_PX on a wide container.
  const tutorMinPctRef = useRef(tutorMinPct);
  tutorMinPctRef.current = tutorMinPct;
  const clampTutor = useCallback((_layout: Layout) => {
    const container = splitContainerRef.current;
    if (!container) return;
    const containerPx = container.clientWidth;
    if (containerPx < EXPANDED_MIN_CONTAINER_PX) return;
    const tutorEl = container.querySelector<HTMLElement>('#tutor');
    const tutorPx = tutorEl?.getBoundingClientRect().width ?? 0;
    if (tutorPx > 0 && tutorPx < TUTOR_MIN_PX) {
      tutorPanelRef.current?.resize(
        `${Math.max(TUTOR_DEFAULT_PCT, tutorMinPctRef.current)}%`,
      );
    }
  }, []);

  useEffect(() => {
    const container = splitContainerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(scheduleViewportCheck);
    ro.observe(container);
    return () => {
      ro.disconnect();
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [scheduleViewportCheck]);

  const toggleOutline = useCallback(() => {
    setOutlineOpen(prev => {
      const next = !prev;
      savePanelState(COURSE_LAYOUT.PANEL_STORAGE_KEY_OUTLINE, next);
      return next;
    });
  }, []);

  // Step 25 (R7.6): auto-close outline on lesson/quiz navigation. 180ms close
  // animation plays concurrently with the content pane crossfade. The close is
  // SESSION-only — we deliberately skip savePanelState so the user's persisted
  // preference (localStorage) is re-applied on reload.
  //
  // StrictMode-safe guard: a plain `useRef(true)` flipped to false on first
  // effect run would CLOSE the outline on React 18 dev double-invoke (first
  // run flips the ref; the StrictMode-triggered second run sees it false and
  // calls setOutlineOpen(false)). Instead, store the previous (lessonId,
  // quizId) tuple and only fire when it actually changes. First run stores
  // the initial tuple and returns; StrictMode's second run sees identical
  // values and returns too. Real navigation (tuple delta) is the only path
  // that triggers the close.
  const prevNavKeysRef = useRef<{ lessonId?: string; quizId?: string } | null>(null);
  useEffect(() => {
    const prev = prevNavKeysRef.current;
    prevNavKeysRef.current = { lessonId, quizId };
    if (prev === null) return;
    if (prev.lessonId === lessonId && prev.quizId === quizId) return;
    setOutlineOpen((open) => (open ? false : open));
  }, [lessonId, quizId]);

  // Step 25 (R7.6) + Step 26 (R7.5): route-transition focus + scroll contract.
  //
  // Two halves of one behavior, run on every `contentKey` change:
  //   1. SYNC scroll-reset: zero `#course-content.scrollTop` the moment the
  //      new route commits. Child routes (LessonViewer) reset their own
  //      inner scrollRef on their own cadence — this is the outer-container
  //      reset so editor routes + quiz routes (no nested scroll container)
  //      also start at the top.
  //   2. rAF FOCUS-MOVE (R7.5): move focus to the scroll container so
  //      keyboard PageDown / arrows work from the first keystroke. Defers to
  //      the next frame so React has committed the new `<Outlet />` subtree;
  //      skips if the outlet already claimed focus for its own field
  //      (LessonEditorPanel's first input, etc.). Deliberately NOT the tutor
  //      composer (per R7.5 "Not to the tutor composer").
  //
  // Merged into one effect because both halves share the same dep + ref and
  // future edits must reason about their ordering (sync reset then rAF focus
  // so the focus outline, if any, renders at scrollTop=0). A ref is used in
  // preference to `document.getElementById('course-content')` — the id
  // wouldn't be safe if any other tree-level code ever introduces a second
  // `#course-content`. Same isPhone/desktop single-branch-mount invariant as
  // `fadeRef`.
  const contentScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = contentScrollRef.current;
    if (!el) return;
    el.scrollTop = 0;
    const id = requestAnimationFrame(() => {
      const ae = document.activeElement as HTMLElement | null;
      if (!ae) {
        el.focus({ preventScroll: true });
        return;
      }
      // Never yank focus from a control the user is actively interacting with.
      // `el.contains(ae)` covers fields inside the outlet (LessonEditorPanel's
      // first input). The second check covers SIBLING panes in the SplitView —
      // the tutor composer (`<textarea>` inside `AiTutorPanel`) lives outside
      // `#course-content`, so without this guard the route-change focus move
      // would steal focus from a user mid-keystroke in the tutor. Adversarial
      // review flagged this. The `contenteditable` match catches the tutor
      // composer's rich-text implementation if it ever switches away from
      // `<textarea>`.
      if (el.contains(ae)) return;
      if (
        ae instanceof HTMLInputElement ||
        ae instanceof HTMLTextAreaElement ||
        ae.isContentEditable
      ) {
        return;
      }
      el.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(id);
  }, [contentKey]);

  // Step 25 (R7.6): mirror `outlineOpen` onto the aside's `inert` attribute
  // imperatively. React 18.3's JSX types don't yet expose `inert` (React 19+
  // does), so using a ref + toggleAttribute avoids a ts-ignore cast. Paired
  // with `aria-hidden` on the aside — `inert` pulls focusable buttons out
  // of the tab order and a11y tree when the outline is closed, satisfying
  // WCAG/axe ("focusable element inside aria-hidden subtree" violation).
  //
  // Step 66: `useLayoutEffect` (not `useEffect`) so the attribute commits
  // synchronously after DOM mutation, BEFORE the browser paints. `useEffect`
  // would run AFTER paint, leaving a microtask window where assistive tech
  // could traverse focusable buttons inside a closed outline (or skip
  // visible buttons in a just-opened one). Identical API, eliminates the
  // pre-paint inconsistency between visible state and the a11y tree.
  const outlineAsideRef = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const el = outlineAsideRef.current;
    if (!el) return;
    if (outlineOpen) el.removeAttribute('inert');
    else el.setAttribute('inert', '');
  }, [outlineOpen]);

  // Step 25 (R7.6): replay the content-pane fade-in animation on content-id
  // change WITHOUT remounting children. Keying the wrapper on contentKey
  // would also remount LessonViewer/QuizViewer, which in turn remounts
  // CourseIdentityStrip on every lesson nav — defeating its chapter.id key
  // (the strip should only slide on chapter transitions, not on lesson↔
  // lesson within a chapter). The ref+classlist-toggle pattern keeps the
  // subtree stable and re-triggers the CSS animation by removing the
  // `animate-in fade-in` classes, forcing a reflow, and re-adding them.
  //
  // INVARIANT: `fadeRef` is attached to two divs in the JSX below (one in
  // the isPhone branch, one in the desktop branch). This only works because
  // `isPhone` short-circuit render guarantees exactly ONE of the two mounts
  // at any time. If a future refactor ever renders both simultaneously
  // (e.g., CSS-only responsive visibility), React will silently overwrite
  // the ref to whichever mounts last and the fade will stop firing on the
  // other. Keep the isPhone ternary render in place OR refactor into a
  // single `<ContentFade>` child that owns its own ref.
  const fadeRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = fadeRef.current;
    if (!el) return;
    el.classList.remove('animate-in', 'fade-in');
    // Force reflow so the browser commits the removal before the re-add
    // on the same tick — without this the class churn is optimized away
    // and the animation doesn't restart.
    void el.offsetHeight;
    el.classList.add('animate-in', 'fade-in');
  }, [contentKey]);

  // Mobile-only: toggle the bottom-sheet tutor. No-op on desktop.
  const toggleTutor = useCallback(() => {
    setTutorOpen(prev => {
      const next = !prev;
      savePanelState(COURSE_LAYOUT.PANEL_STORAGE_KEY_TUTOR, next);
      return next;
    });
  }, []);

  /** Mobile-only: force-open the bottom-sheet tutor. */
  const openTutor = useCallback(() => {
    setTutorOpen(prev => {
      if (!prev) savePanelState(COURSE_LAYOUT.PANEL_STORAGE_KEY_TUTOR, true);
      return true;
    });
  }, []);

  /**
   * Ref-based callback for sending quoted text to the tutor panel.
   * AiTutorPanel registers its setQuotedText via this ref.
   */
  const setQuotedTextRef = useRef<((text: string, action?: string) => void) | null>(null);
  const pendingTutorMessageRef = useRef<{ text: string; action: string } | null>(null);

  const registerQuotedText = useCallback((setter: ((text: string, action?: string) => void) | null) => {
    setQuotedTextRef.current = setter;
    if (setter && pendingTutorMessageRef.current) {
      const { text, action } = pendingTutorMessageRef.current;
      pendingTutorMessageRef.current = null;
      setter(text, action);
    }
  }, []);

  const sendToTutor = useCallback((action: string, text: string) => {
    // Mobile: open the bottom-sheet so the panel is visible.
    setTutorOpen(prev => {
      if (!prev) savePanelState(COURSE_LAYOUT.PANEL_STORAGE_KEY_TUTOR, true);
      return true;
    });
    pendingTutorMessageRef.current = { text, action };
    if (setQuotedTextRef.current) {
      const pending = pendingTutorMessageRef.current;
      pendingTutorMessageRef.current = null;
      setQuotedTextRef.current(pending.text, pending.action);
    }
  }, []);

  // Ref for injecting transient system messages into AiTutorPanel.
  const transientMessageRef = useRef<((content: string) => void) | null>(null);
  const pendingTransientRef = useRef<string | null>(null);
  const sendTransientToTutor = useCallback((content: string) => {
    if (transientMessageRef.current) {
      transientMessageRef.current(content);
    } else {
      pendingTransientRef.current = content;
      setTutorOpen((prev) => {
        if (!prev) savePanelState(COURSE_LAYOUT.PANEL_STORAGE_KEY_TUTOR, true);
        return true;
      });
    }
  }, []);

  // Tablet breakpoint (hide outline, show as sheet)
  const [isTablet, setIsTablet] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${COURSE_LAYOUT.TABLET_BREAKPOINT - 1}px)`);
    setIsTablet(mql.matches);
    const handler = (e: MediaQueryListEvent) => setIsTablet(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Phone breakpoint (<768px): Outlet full-width, tutor in bottom Sheet behind a FAB.
  const [isPhone, setIsPhone] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    setIsPhone(mql.matches);
    const handler = (e: MediaQueryListEvent) => setIsPhone(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Author-variant right pane: which surface is visible (Preview vs AI Tutor).
  // Defaults to 'preview' so authors see their work alongside the editor on mount.
  // State lives at the layout level so switching lessons inside the same CourseLayout
  // instance preserves the author's chosen mode, but cross-variant mounts start fresh.
  const [rightPaneMode, setRightPaneMode] = useState<CourseLayoutRightPaneMode>('preview');

  // The preview node the active child route registers via outlet context. Null when
  // no author route has mounted (or the route is tearing down); in that case we fall
  // back to always rendering the tutor.
  const [previewPane, setPreviewPaneNode] = useState<ReactNode | null>(null);
  const setPreviewPane = useCallback((node: ReactNode | null) => {
    setPreviewPaneNode(node);
  }, []);

  if (!subjectId) return null;
  // FIND-C (2026-04-25): block malformed UUIDs from reaching the lesson/
  // quiz RPCs (which would PostgREST-400 and throw through to the generic
  // ErrorBoundary). The same guard lives in CourseOverview for the
  // overview route — both URL shapes go through here for child routes.
  if (!isValidUuid(subjectId)) return <CourseNotFound />;

  return (
    <ActiveLessonProvider>
      <WorkspaceBreadcrumbProvider>
      <div className="course-layout h-screen overflow-hidden flex flex-col">
        {/* WCAG 2.1 SC 2.4.1 — keyboard skip-link past the header to lesson content. */}
        <a
          href="#course-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:shadow-lg"
        >
          Skip to lesson content
        </a>

        <div className="flex flex-1 overflow-hidden relative">
        {/* Phone: Outlet full-width, tutor in bottom Sheet behind a FAB.
            Tablet/desktop: resizable SplitView with always-visible tutor pane. */}
        {isPhone ? (
          <div
            id="course-content"
            ref={contentScrollRef}
            tabIndex={-1}
            className="flex-1 overflow-y-auto overflow-x-hidden"
          >
            {/* Step 25 (R7.6): phone branch shares the class-toggle fade
                pattern (see fadeRef effect above). Children stay mounted
                across navigation; only the CSS animation replays. */}
            <div
              ref={fadeRef}
              data-testid="course-content-fade"
              // `h-full` preserves the height chain from `#course-content`
              // (a `flex-1 overflow-y-auto` container) down to child routes
              // like `LessonViewer` whose root is `flex flex-col h-full` and
              // needs a concrete parent height for its internal
              // `flex-1 overflow-y-auto` scroll container to bound correctly.
              // Without this the scroll container grows to content height and
              // `useShortLessonDetection` mis-reports every lesson as short
              // (Playwright E2E `lesson-end-of-notes-footer.spec.ts` caught it).
              className="animate-in fade-in h-full"
              // Step 61: reduce → 0ms so contentKey-change replay is invisible.
              style={{ animationDuration: prefersReducedMotion ? '0ms' : `${WORKSPACE_TOKENS.ROUTE_CROSSFADE_MS}ms` }}
            >
              <Outlet context={{
                subjectId,
                variant,
                outlineOpen,
                tutorOpen,
                toggleOutline,
                toggleTutor,
                openTutor,
                sendToTutor,
                sendTransientToTutor,
                isTablet,
                rightPaneMode,
                setRightPaneMode,
                setPreviewPane,
              }} />
            </div>
          </div>
        ) : (
          <div ref={splitContainerRef} className="flex-1 flex min-w-0 overflow-x-hidden">
          <SplitView
            storageKey="iskool:course-content-tutor-split"
            direction="horizontal"
            className="flex-1 overflow-x-hidden"
            onResize={clampTutor}
          >
            <SplitView.Pane id="content" minSize="40%" defaultSize="70%">
              <div className="flex flex-col h-full">
                {/* Unified header sits ABOVE sidebar+lesson, horizontally
                    aligned with the AI tutor pane's own header on the right. */}
                <UnifiedHeader
                  subjectId={subjectId}
                  outlineOpen={outlineOpen}
                  onToggleOutline={toggleOutline}
                />
                <div className="flex flex-1 min-h-0 overflow-hidden">
                  {/* Docked left sidebar — opens BELOW the unified header.
                      Animates width OUTLINE_WIDTH ↔ 0 so chapter rows
                      never leak/clip when collapsed. */}
                  <aside
                    ref={outlineAsideRef}
                    aria-label={variant === 'author' ? 'Course editor outline' : 'Course outline'}
                    aria-hidden={!outlineOpen}
                    data-outline-state={outlineOpen ? 'open' : 'closed'}
                    className="shrink-0 overflow-hidden border-r border-border bg-card transition-[width] ease-in-out"
                    style={{
                      width: outlineOpen ? COURSE_LAYOUT.OUTLINE_WIDTH : 0,
                      // Step 25 (R7.6): 180ms close animation (R5 spec) matches
                      // content-pane crossfade timing so both finish together.
                      // Step 61: reduce → 0ms so width snaps without animation.
                      transitionDuration: prefersReducedMotion ? '0ms' : `${WORKSPACE_TOKENS.OUTLINE_CLOSE_ANIM_MS}ms`,
                    }}
                  >
                    <div
                      style={{
                        width: COURSE_LAYOUT.OUTLINE_WIDTH,
                        transitionDuration: prefersReducedMotion ? '0ms' : `${WORKSPACE_TOKENS.OUTLINE_CLOSE_ANIM_MS}ms`,
                      }}
                      className={cn(
                        'h-full transition-opacity',
                        outlineOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
                      )}
                    >
                      <Suspense fallback={<div className="animate-pulse h-full p-3" />}>
                        {variant === 'author' ? (
                          <AuthorOutlineSidebar subjectId={subjectId} />
                        ) : (
                          <CourseOutlineSidebar subjectId={subjectId} />
                        )}
                      </Suspense>
                    </div>
                  </aside>
                  <div
                    id="course-content"
                    ref={contentScrollRef}
                    tabIndex={-1}
                    className="flex-1 overflow-y-auto overflow-x-hidden"
                  >
                    {/* Step 25 (R7.6): class-toggle fade wrapper. The fadeRef
                        effect above removes `animate-in fade-in`, forces a
                        reflow, and re-adds them whenever contentKey changes —
                        replaying the 200ms fade-in CSS animation WITHOUT
                        remounting the Outlet subtree. Keeping children
                        mounted is load-bearing: LessonViewer renders
                        CourseIdentityStrip with `key={chapter.id}`, so a
                        remount here would flatten that chapter-key into
                        per-lesson remounts and the strip would slide on
                        every lesson nav instead of only on chapter→chapter. */}
                    <div
                      ref={fadeRef}
                      data-testid="course-content-fade"
                      // `h-full` preserves the height chain from
                      // `#course-content` down to child routes (LessonViewer
                      // uses `flex flex-col h-full`). Without it the inner
                      // scrollRef grows to content height and
                      // useShortLessonDetection mis-reports every lesson as
                      // short — see isPhone branch above.
                      className="animate-in fade-in h-full"
                      // Step 61: reduce → 0ms (mirror isPhone branch above).
                      style={{ animationDuration: prefersReducedMotion ? '0ms' : `${WORKSPACE_TOKENS.ROUTE_CROSSFADE_MS}ms` }}
                    >
                      <Outlet context={{
                        subjectId,
                        variant,
                        outlineOpen,
                        tutorOpen,
                        toggleOutline,
                        toggleTutor,
                        openTutor,
                        sendToTutor,
                        sendTransientToTutor,
                        isTablet,
                        rightPaneMode,
                        setRightPaneMode,
                        setPreviewPane,
                      }} />
                    </div>
                  </div>
                </div>
              </div>
            </SplitView.Pane>

            <SplitView.Handle />
            {/* react-resizable-panels v4: numeric sizes are PIXELS, strings are
                PERCENTAGES. We use percentages so the pane scales with viewport.
                NOT collapsible — the chat pane can never be hidden. */}
            <SplitView.Pane
              id="tutor"
              minSize={`${tutorMinPct}%`}
              maxSize="50%"
              defaultSize={`${TUTOR_DEFAULT_PCT}%`}
              panelRef={tutorPanelRef}
              className="border-l border-border bg-card overflow-x-hidden min-w-0"
            >
              {variant === 'author' ? (
                // Author variant: ALWAYS wrap AiTutorPanel in <Tabs>/<TabsContent
                // value="tutor"> so its tree position is stable regardless of
                // whether a preview has been registered. Earlier impl swapped
                // between Tabs (preview registered) and top-level Suspense (no
                // preview) — React treats those as different tree positions and
                // remounts AiTutorPanel on the null→set transition, losing unsent
                // messages / streaming state / quoted text. Seer flagged this;
                // fix keeps the tutor node in the same spot across previewPane
                // state changes. TabsList + Preview content render only when a
                // preview is registered, but the Tutor content stays anchored.
                //
                // forceMount on both TabsContent preserves state across tab
                // switches once both tabs exist (Radix otherwise unmounts
                // inactive tabs; typed tutor input and mid-playback video would
                // reset on every switch).
                <Tabs
                  value={previewPane !== null ? rightPaneMode : 'tutor'}
                  onValueChange={(v) => setRightPaneMode(v as CourseLayoutRightPaneMode)}
                  className="flex flex-col h-full"
                >
                  {previewPane !== null && (
                    <TabsList className="border-b border-border shrink-0 px-2">
                      <TabsTrigger value="preview" className="py-2 text-sm">Preview</TabsTrigger>
                      <TabsTrigger value="tutor" className="py-2 text-sm">AI Tutor</TabsTrigger>
                    </TabsList>
                  )}
                  {previewPane !== null && (
                    <TabsContent
                      value="preview"
                      forceMount
                      className={cn(
                        'mt-0 flex-1 min-h-0 overflow-y-auto data-[state=inactive]:hidden',
                      )}
                    >
                      {previewPane}
                    </TabsContent>
                  )}
                  <TabsContent
                    value="tutor"
                    forceMount
                    className={cn(
                      'mt-0 flex-1 min-h-0',
                      previewPane !== null && 'data-[state=inactive]:hidden',
                    )}
                  >
                    <Suspense fallback={
                      <div className="h-full flex items-center justify-center">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:0ms]" />
                          <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:150ms]" />
                          <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:300ms]" />
                        </div>
                      </div>
                    }>
                      <AiTutorPanel
                        key={subjectId}
                        subjectId={subjectId}
                        onRegisterQuotedText={registerQuotedText}
                        onRegisterTransientMessage={(setter) => {
                          transientMessageRef.current = setter;
                          if (setter && pendingTransientRef.current) {
                            const pending = pendingTransientRef.current;
                            pendingTransientRef.current = null;
                            setter(pending);
                          }
                        }}
                      />
                    </Suspense>
                  </TabsContent>
                </Tabs>
              ) : (
                // Student variant: single-pane tutor, no Tabs wrapper needed
                // since there's no preview toggle surface.
                <Suspense fallback={
                  <div className="h-full flex items-center justify-center">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:0ms]" />
                      <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:150ms]" />
                      <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:300ms]" />
                    </div>
                  </div>
                }>
                  <AiTutorPanel
                    key={subjectId}
                    subjectId={subjectId}
                    onRegisterQuotedText={registerQuotedText}
                    onRegisterTransientMessage={(setter) => {
                      transientMessageRef.current = setter;
                      if (setter && pendingTransientRef.current) {
                        const pending = pendingTransientRef.current;
                        pendingTransientRef.current = null;
                        setter(pending);
                      }
                    }}
                  />
                </Suspense>
              )}
            </SplitView.Pane>
          </SplitView>
          </div>
        )}
      </div>

      {/* Phone: floating Bot FAB + bottom-sheet tutor. */}
      {isPhone && (
        <>
          {!tutorOpen && (
            <Button
              variant="default"
              size="icon"
              onClick={toggleTutor}
              aria-label="Open AI tutor"
              className="fixed bottom-20 right-4 z-40 h-12 w-12 rounded-full shadow-lg"
            >
              <Bot className="h-5 w-5" />
            </Button>
          )}
          <Sheet open={tutorOpen} onOpenChange={(open) => {
            setTutorOpen(open);
            savePanelState(COURSE_LAYOUT.PANEL_STORAGE_KEY_TUTOR, open);
          }}>
            <SheetContent side="bottom" className="h-[85vh] p-0 flex flex-col">
              <SheetHeader className="px-4 py-3 border-b border-border shrink-0">
                <SheetTitle className="text-sm">AI Tutor</SheetTitle>
              </SheetHeader>
              <div className="flex-1 overflow-hidden">
                <Suspense fallback={
                  <div className="h-full flex items-center justify-center">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:0ms]" />
                      <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:150ms]" />
                      <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:300ms]" />
                    </div>
                  </div>
                }>
                  <AiTutorPanel
                    key={subjectId}
                    subjectId={subjectId}
                    onRegisterQuotedText={registerQuotedText}
                    onRegisterTransientMessage={(setter) => {
                      transientMessageRef.current = setter;
                      if (setter && pendingTransientRef.current) {
                        const pending = pendingTransientRef.current;
                        pendingTransientRef.current = null;
                        setter(pending);
                      }
                    }}
                  />
                </Suspense>
              </div>
            </SheetContent>
          </Sheet>
        </>
      )}

      <CourseOnboardingCard />
      <KeyboardShortcutCheatsheet />
      </div>
      </WorkspaceBreadcrumbProvider>
    </ActiveLessonProvider>
  );
};

/**
 * Reads the workspace breadcrumb published by the active route (LessonViewer,
 * QuizViewer) and renders the unified top header.
 */
function UnifiedHeader({
  subjectId,
  outlineOpen,
  onToggleOutline,
}: {
  subjectId: string;
  outlineOpen: boolean;
  onToggleOutline: () => void;
}) {
  const value = useWorkspaceBreadcrumb();
  const breadcrumb = value?.breadcrumb ?? {
    subject: '',
    subjectHref: `/classroom/course/${subjectId}`,
  };
  return (
    <WorkspaceHeader
      breadcrumb={breadcrumb}
      outlineOpen={outlineOpen}
      onToggleOutline={onToggleOutline}
    />
  );
}

export const CourseLayout = memo(CourseLayoutComponent);
CourseLayout.displayName = 'CourseLayout';
